import { useState, useEffect, useRef, useCallback } from "react";
import { TableLoader } from "../../../components/LoaderC";
import styles from "./leases.module.css";
import LeaseCard from "./component/LeaseCard";
import LeaseFilter from "../components/Filter";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 12;

const isActiveFilter = (value) => value !== "" && value !== "all" && value !== undefined && value !== null;

const LeaseGridView = ({ handleView, filterData, setFilterData, isGridView }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [gridData, setGridData] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState(filterData?.searchKey || "");

    const sentinelRef = useRef(null);
    const requestSeq = useRef(0);
    const hasLoadedOnceRef = useRef(false);

    const property = filterData?.property;
    const apartment = filterData?.apartment;
    const status = filterData?.status;

    const fetchLeases = useCallback(async (currentOffset, replace) => {
        const seq = ++requestSeq.current;

        let activeIndicator = "loadingMore";
        if (replace && !hasLoadedOnceRef.current) {
            activeIndicator = "contentLoading";
            setContentLoading(true);
        } else if (replace) {
            activeIndicator = "refetching";
            setRefetching(true);
        } else {
            setLoadingMore(true);
        }
        setLoadMoreError(false);

        let params = { offset: currentOffset, limit: currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE };
        if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
        if (isActiveFilter(property)) params.property_id = property;
        if (isActiveFilter(apartment)) params.apt_id = apartment;
        if (isActiveFilter(status)) params.status = status;

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/leases-paginated", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: params,
            });

            if (seq !== requestSeq.current) return;

            const newChunk = response.data?.data || [];
            const meta = response.data?.meta || {};

            setGridData((prev) => (replace ? newChunk : prev.concat(newChunk)));
            setOffset(meta?.next_offset ?? currentOffset + newChunk.length);
            setHasMore(Boolean(meta?.has_more));

            setLoadError(false);
            hasLoadedOnceRef.current = true;
        } catch (error) {
            if (seq !== requestSeq.current) return;
            console.error("[Leases] Failed to load lease grid", error);
            toast.error(error?.response?.data?.message || "Unable to load leases. Please try again.");

            if (replace) {
                setLoadError(true);
                setGridData([]);
                setHasMore(false);
            } else {
                setLoadMoreError(true);
            }
        } finally {
            if (seq === requestSeq.current) {
                if (activeIndicator === "contentLoading") setContentLoading(false);
                else if (activeIndicator === "refetching") setRefetching(false);
                else setLoadingMore(false);
            }
        }
    }, [getAccessTokenSilently, debouncedSearch, property, apartment, status]);

    // Debounce search text input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(filterData?.searchKey || "");
        }, 400);

        return () => clearTimeout(handler);
    }, [filterData?.searchKey]);

    // Load first chunk when search, filters change or grid view becomes active
    useEffect(() => {
        if (!isGridView) return;
        fetchLeases(0, true);
    }, [isGridView, fetchLeases]);

    // Infinite scroll: load next chunk when sentinel enters viewport
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !isGridView) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
                fetchLeases(offset, false);
            }
        }, { threshold: 0.1 });

        observer.observe(node);
        return () => observer.disconnect();
    }, [isGridView, hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchLeases]);

    return (<div className={`${styles.BodyCon}`}>
        <div className={`${styles.mainCardDiv}`}>
            <div className={`${styles.mainCard}`}>
                <div className="control-pane">

                    <LeaseFilter isGridView={isGridView} handleView={handleView.bind(this)} filterData={filterData} setFilterData={setFilterData.bind(this)} />

                    {contentLoading && (<div className="Loader TableLoader"><TableLoader /></div>)}

                    {!contentLoading && (<div className={refetching ? `${styles.NotesRow} ${styles.Refetching}` : `${styles.NotesRow}`}>

                        {gridData.map((item) => {
                            return (<LeaseCard key={item?.list_id || item?.id} item={item} />);
                        })}

                        {/* Sentinel: entering viewport loads next chunk */}
                        <div ref={sentinelRef} className={styles.ScrollSentinel} />

                        {loadingMore && (
                            <div className={styles.gridScrollStatus}>
                                <div className={styles.scrollLoaderWrap}>
                                    <div className={styles.scrollSpinner} />
                                    <span>Loading more leases...</span>
                                </div>
                            </div>
                        )}

                        {loadMoreError && (
                            <div className={styles.gridScrollStatus}>
                                <p>Couldn't load more leases.</p>
                                <button type="button" className={styles.RetryBu} onClick={() => fetchLeases(offset, false)}>Retry</button>
                            </div>
                        )}

                        {loadError && (<div className={`${styles.NoDataMain}`}>
                            <p className={`${styles.NoDataText}`}>Something went wrong!</p>
                            <p className={`${styles.NoDataTextSub}`}>we couldn't load the leases.</p>
                            <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchLeases(0, true)}>Retry</button>
                        </div>)}

                        {!loadError && gridData.length === 0 && (<div className={`${styles.NoDataMain}`}>
                            <div className={`${styles.NoDataIMG}`}>
                                <img src="/static/img/no-lease.png" alt="No Data" />
                            </div>
                            <p className={`${styles.NoDataText}`}>Unfortunately!</p>
                            <p className={`${styles.NoDataTextSub}`}>we couldn't find any leases at this time.</p>
                        </div>)}

                    </div>)}

                </div>
            </div>
        </div>
    </div >)
}

export default LeaseGridView;
